import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import { useVatMode } from './useVatMode';
import useSubmitGuard from './useSubmitGuard';
import useListRefreshWhenClean from './useListRefreshWhenClean';
import useIdempotencyKey from './useIdempotencyKey';
import useDirtyContinuation from './useDirtyContinuation';
import useExpandedOrderCore from './useExpandedOrderCore';
import { apiFetch, apiGet, API_BASE } from '../utils/api';
import {
  useOrderReferenceArmed,
  useOrderAccountingLists,
  useOrderReferenceData,
} from './useOrderPickerLists';
import {
  DELIVERY_STAGE_STATUSES,
  RECEIVE_ENABLED_STATUSES,
  RECEIVING_LOCKED_TOOLTIP,
  managerMayCancelOrder,
  requesterMayCancelOrder,
  isOrderComplete,
  canMarkOrderDone,
  canRestoreOrderDone,
  MARK_ORDER_DONE_TOOLTIP,
} from '../constants/statuses';
import { buildCompleteConfirmMessage, buildReopenConfirmMessage } from '../constants/orderCompletionPrompt';
import ConfirmDialog from '../components/ConfirmDialog';
import { canPokeOrder, pokeLabel, pokeTitle } from '../utils/orderStepInfo';
import { refreshOneOrder, refreshOrderRow, mergeStatusJumpResult, withOrderMutationNotice } from '../utils/refreshOneOrder';
import { makeReceivedItemHandlers } from '../utils/receivedItems';
import { useToastOrNoop } from '../contexts/ToastContext';
import { useDepartment } from '../contexts/DepartmentContext';
import { useOrderAuthority } from './useOrderAuthority';
// 🔻 `OrderPdfButton` WAS IMPORTED HERE AND IS GONE (2026-08-16), moved into
// the shared `Actions ▾` — see the ACTIONS MENU box in `pages/CLAUDE.md`. Its
// three actions are now rows inside that menu (`OrderPdfMenuItems`), exactly
// as `DepartmentOrders.js` and `Approvals.js` already did on 2026-08-10. The
// component itself is untouched and still serves `OrderPage.js`.
import OrderActionsMenu from '../components/OrderActionsMenu';
import { withHireMenu } from '../components/hires/hireOrderMenu';
import OrderPdfMenuItems from '../components/OrderPdfMenuItems';
// THE status override — the SAME component `DepartmentOrders.js` renders as its
// actions-menu footer, and the reason this page needs no Approve/Mark-Ordered
// buttons of its own. See `statusJumpNode` below.
import InlineStatusJump from '../components/InlineStatusJump';
import { orderLifecycleMenuItem } from '../constants/orderLifecycleMenuItems';
// 🔻 `OrderExpandedDetails` WAS IMPORTED AND RENDERED DIRECTLY HERE AND IS GONE
// (Phase 2, My Orders / Dept Orders convergence). It is now embedded INSIDE
// the shared `OrderExpandedRow` (both branches — read-only and inline-editor),
// exactly as `DepartmentOrders.js` already relies on. This page no longer
// renders it itself.
import OrderExpandedRow from '../components/OrderExpandedRow';
import { getOrderNumber } from '../utils/orderTitle';
import { countDelivery, allGoodsReceived } from '../utils/deliveryCountableItems';
import { orderHasHireStillOut } from '../utils/hireBuckets';
import InternalNotesReadOnly from '../components/InternalNotesReadOnly';

/**
 * `useMyOrderExpansion` — THE My Orders expanded row: its state, its handlers,
 * its completion dialog and `renderExpanded(order)`, the node the TABLE row
 * renders (the card row went with the order cards, 2026-10-18).
 *
 * 🔀 EXTRACTED 2026-09-27, VERBATIM, out of `pages/MyOrders.js` so My Hires can
 * expand a hire into the SAME panel My Orders shows for that order. Every
 * gate, handler and dialog below is the page's own code moved, not rewritten;
 * every `MyOrders.*` test is the parity proof, and the archaeology travelled
 * with each block.
 *
 * 🔑 **A SEPARATE HOOK FROM `useDeptOrderExpansion`, ON PURPOSE — the two
 * pages' expansions differ in every handler and gate, not in a flag.** My
 * Orders drives `handleQuickAction` (the owner's own lifecycle, `PATCH
 * /:id/status` in My Space, cancel/delete/duplicate), a flat `my-` menu, a
 * display-only Internal-notes node and one completion dialog; Department
 * Orders drives `handleAction` + save-then-act + four dialogs. One hook with
 * options would be two hooks sharing a name.
 *
 * 🔑 **WHAT STAYS ON THE CALLING PAGE — the LIST concerns**, each reading the
 * `editDirtyId` the page owns: the order list and `fetchOrders`, the poll's
 * `paused`, the leave guard, the department-switch dirty registration
 * and the deep link. `onListRefresh` is what the tick cluster calls after a
 * tick.
 *
 * 🚨 **IT RESOLVES NOTHING NEW** — the server's `detail.can_edit` and
 * `useOrderAuthority().canManageOrder` exactly where the page read them.
 *
 * ⚠️ `setOrders`, `setEditDirtyId` must be `useState` setters and `fetchOrders`
 * a stable callback; they appear in dependency arrays here.
 *
 * 🧾 **THE EDITS THE MOVE MADE TO THE MOVED CODE — the whole list** (commit
 * 831fa6af, and 8908ba12 for the last):
 * - `setOrders` / `setEditDirtyId` added to six dependency arrays (now
 *   parameters; both `useState` setters, so identity is unchanged);
 * - the hook calls its OWN `useNavigate()`, `useToastOrNoop()` and
 *   `useDepartment()` (destructuring only the names it reads) — a second
 *   instance of each beside the page's;
 * - `canPokeOrder(order, currentUserId)` was then computed in BOTH places: the
 *   page's card node (retired 2026-10-18) and this hook's quick actions;
 * - `deferReferenceData` (opt-in, default `false` = unchanged): a guard and a
 *   dependency on the two reference-data effects, for the hires Table;
 * - three import-block notes were DROPPED in the move (the `OrderPdfButton`
 *   and `OrderExpandedDetails` "IS GONE" notes, and the status-override
 *   clause "and the reason this page needs no Approve/Mark-Ordered buttons of
 *   its own") and restored VERBATIM from `4a1fcb27`'s `pages/MyOrders.js` in
 *   the review fix round — so "this page" in them means My Orders.
 */
export default function useMyOrderExpansion({
  setOrders: setOrdersFromCaller,
  fetchOrders,
  editDirtyId,
  setEditDirtyId,
  currentUser,
  currentUserId,
  onListRefresh: handleItemsReceived,
  // 🔀 2026-10-29 (review T-1) — `(orderId, busy)` at a tick's start and end, so the page's
  // `hooks/useHeldOpenRows.js` holds the row BEFORE the PATCH. Default `null`: no caller changes.
  onTickBusyChange = null,
  deferReferenceData = false,
  // 🔀 2026-09-28 — the hires pages' Off hire all + per-line hire actions;
  // same contract as `useDeptOrderExpansion`'s `hireActions`. Default `null`:
  // My Orders passes nothing and renders byte-identically.
  hireActions = null,
  // 🔀 2026-09-29 — the hires pages' "an order changed" report; same contract
  // as `useDeptOrderExpansion`'s `onOrderMutated`. Default `null`: My Orders
  // passes nothing and `setOrders` below is its own setter, unchanged.
  onOrderMutated = null,
  // 🔀 2026-09-30 — My Orders' personal "Mark as done" / "Restore" (owner
  // ruling): `(order, done) => void`, the PAGE does the PATCH and the row
  // update. Default `null`: no item renders, so My Hires' menu is unchanged.
  onPersonalDone = null,
}) {
  const setOrders = useMemo(
    () => withOrderMutationNotice(setOrdersFromCaller, onOrderMutated),
    [setOrdersFromCaller, onOrderMutated]
  );
  // 🔀 2026-09-29 — the Status Override's silent list refetch, deferred while
  // the caller's edit is dirty (the poll's rule); `useDeptOrderExpansion`'s twin.
  const requestListRefresh = useListRefreshWhenClean(() => fetchOrders(true), editDirtyId !== null);
  const navigate = useNavigate();
  // What this hook reads from the department context: `activeDepartment` for
  // the reference-data effects, the switch clear and `isPersonalWorkspace`;
  // `contextIsAdmin` + `assignments` for `userAssignedProductions`.
  const { isAdmin: contextIsAdmin, activeDepartment, assignments } = useDepartment();
  const { error: toastError } = useToastOrNoop();


  // Per order, in the order's own department. The SERVER endpoint this page
  // calls (`GET /api/orders?user_id=`) still returns the caller's own orders
  // across EVERY department they belong to — `fetchOrders` narrows what is
  // DISPLAYED to `activeDepartment` (a display filter, not an authority
  // change) — so the active department's capabilities remain the wrong
  // question here.
  const { canManageOrder } = useOrderAuthority();

  // Is the viewer STANDING IN their My Space? (2026-09-11)
  //
  // 🔑 The SCOPE LIMITER on the Cancel widening below, and nothing else. Every
  // row this page renders is already narrowed to `activeDepartment` by
  // `fetchOrders`'s display filter, so "the active department is personal"
  // is equivalent to "every order in view is a My Space order" — which is why
  // no per-row field is needed and none was added to the payload.
  //
  // 🪤 `production_is_personal`, NEVER `department_is_personal` (they disagree
  // on 10 dev rows; `utils/personalSpace.js` carries the measurement) and never
  // a name match — department and production names may duplicate. The same read
  // the display filter above already makes.
  //
  // ⚠️ It is NOT an authority term. Authority is `canManageOrder`, which reads
  // the server-computed `can_manage`; this only says WHERE the viewer is.
  const isPersonalWorkspace = !!(activeDepartment && activeDepartment.production_is_personal);

  const { formatMoney } = useVatMode();

  // Expandable row state + every write to the detail cache, shared with
  // `useDeptOrderExpansion` and `pages/Approvals.js` since 2026-10-03.
  // 🔑 `useExpandedOrderCore` runs NO effect, so calling it here cannot move
  // this page's mount request order — which the parity goldens record, and
  // which an earlier attempt at this extraction did silently reorder. See its
  // docblock.
  const {
    expandedOrders, setExpandedOrders,
    orderDetails, setOrderDetails,
    detailsLoading, setDetailsLoading,
    pokeLoading,
    updateOrderListFromItem,
    handleCostCodeChange,
    handlePaymentTypeChange,
    handleItemNotifyUpdated,
    handlePoNumberSaved,
    handleExpectedDeliverySaved,
    handleOrderFilesChanged,
    handleOrderDetailInvalidated,
    handlePoke,
    requestOrderReason,
    requestRejectionReason,
    reasonDialog,
  } = useExpandedOrderCore({ setOrders });
  // THE STATUS OVERRIDE's `onSaved` — `useDeptOrderExpansion#handleStatusJumpSaved`'s
  // twin: the merge, `sfx-approval-change` (the bell / sidebar dots), and the
  // list refetch (a hires page's notifying `setOrders` has already asked).
  const handleStatusJumpSaved = useCallback((updated) => {
    mergeStatusJumpResult(updated, setOrders, setOrderDetails);
    window.dispatchEvent(new Event('sfx-approval-change'));
    if (!onOrderMutated) requestListRefresh();
  }, [setOrders, onOrderMutated, requestListRefresh]);
  const [costCodes, setCostCodes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  // Reference data the inline edit form's dropdowns need — fetched ONCE PER
  // PAGE, byte-identical in shape to the effect on `DepartmentOrders.js`
  // (see below). Empty until Phase 2 added the inline editor to this page.
  const [productions, setProductions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [gags, setGags] = useState([]);
  // 🚨 WHICH REFERENCE LISTS ARE EMPTY BECAUSE THE REQUEST FAILED — 2026-08-22.
  //
  // `[]` is the shape of "this department has none" AND of "nobody asked", and
  // `components/OrderForm.js` makes confident claims from it in the inline
  // editor: *"No suppliers set up for this department yet"* (worded to send the
  // reader off to CREATE supplier records), and — via an emptied option list —
  // every coded line silently redrawn as `-- None --`. See `OrderForm`'s
  // `loadFailures` prop for the full account.
  const [loadFailures, setLoadFailures] = useState({});

  // What `OrderForm` calls `userAssignedProductions` — null = admin / still
  // loading, [] = this user holds no assignments at all (the form then blocks
  // submission with an explanation). Computed exactly as `DepartmentOrders.js`
  // does it — form plumbing, not an authority decision.
  const userAssignedProductions = contextIsAdmin
    ? null
    : (assignments && assignments.length === 0 ? [] : null);

  // ── THE UNSAVED-EDIT CONTINUATION GUARD (2026-09-28) ─────────────────────
  // 🔑 The SAME `hooks/useDirtyContinuation.js` as Department Orders, for every
  // control that would destroy an open inline edit BY INTENT — collapsing the
  // row, expanding another one, switching Cards⇄Table, and a navigating action
  // (Edit; View Full Details until it was DELETED 2026-10-04, owner ruling —
  // kept only for My Hires, gated on `hireActions`). Clean → it runs; dirty → the editor's three-way
  // prompt, then the click on Save (ok) or Discard. Replaced a copied
  // `blockIfDirty` (refuse + an amber pulse the owner could not see). The
  // PAGE owns `editDirtyId` here, so the hook reads the one passed in.
  const {
    continueWhenClean,
    promptSignal: collapseRequestSignal,
    onPromptResolved,
  } = useDirtyContinuation({ dirty: editDirtyId !== null });
  const toggleExpandRef = useRef(null);

  // Dirty reporting from an open inline editor, keyed by row id so a closing
  // panel's unmount cleanup can only clear its own flag.
  const handleEditorDirtyChange = useCallback((orderId, isDirty) => {
    setEditDirtyId(prev => (isDirty ? orderId : prev === orderId ? null : prev));
  }, [setEditDirtyId]);
  // 🪤 `handleEditorSaved` itself is defined further down, immediately after
  // `fetchOrders` — it closes over `fetchOrders`, which is declared later in
  // this component, so it cannot live in this block without a temporal-dead-
  // zone error the moment `useCallback`'s dependency array evaluates it.

  // ── DROP PER-DEPARTMENT EXPAND/EDIT STATE ON A SWITCH (2026-08-30) ──────
  // `DepartmentOrders.js` has carried this since 2026-08-17; `MyOrders.js`
  // never had it. `fetchOrders`'s `activeDepartment?.department_id`
  // dependency already re-issues the list request for the new department;
  // this clears the transient view-state at the MOMENT of the switch.
  //
  // 🕶️ **MEASURED, NOT ASSUMED — do not restate this as "editDirtyId stays
  // stuck forever". See `Approvals.departmentSwitchDirtyGuard.test.js`'s
  // docblock for the full account (this page has the identical shape):**
  // `fetchOrders` sets `loading` non-silent on every switch, `if (loading) { …
  // LoadingSpinner … }` (below) unmounts the WHOLE page including any open
  // `OrderInlineEditor`, and that component's own unmount cleanup already
  // clears `editDirtyId` regardless of this effect — so the poll's `paused`
  // self-heals too. **What
  // genuinely does NOT self-heal, because it is plain page state rather than
  // anything tied to a child unmounting: `expandedOrders` and
  // `orderDetails`.** Without this effect, switching away and back
  // re-expands a row nobody clicked, showing a snapshot cached BEFORE the
  // round trip (`toggleExpand`'s own `!orderDetails[orderId]` guard skips the
  // refetch entirely) — a real, measured, non-vacuous bug this effect closes.
  // Owner ruling on WHY a dirty edit does not block a switch, and the newer
  // one on why it is announced first rather than dropped silently, are both
  // at `DepartmentOrders.js`'s twin effect — read there, not re-argued here;
  // they still apply to `editDirtyId` even though its OWN staleness turned
  // out to be self-healing on this particular page.
  // 🪤 `useRef` seeded with the current id: a no-op on first render and on the
  // initial undefined→id resolution, so it never disturbs a single-department
  // session and never double-fetches.
  const prevSwitchDeptRef = useRef(activeDepartment?.department_id);
  useEffect(() => {
    const deptId = activeDepartment?.department_id;
    if (prevSwitchDeptRef.current === deptId) return;
    prevSwitchDeptRef.current = deptId;
    setExpandedOrders(new Set());
    setOrderDetails({});
    setDetailsLoading({});
    setEditDirtyId(null);
    setCompletionPrompt(null);
  }, [activeDepartment?.department_id, setEditDirtyId]);

  // Save landed: drop the guard, collapse the row, then refresh BOTH this
  // order's cached DETAIL and the list — same sequence as
  // `DepartmentOrders.js`'s `handleEditorSaved`. `{ clearCanAction: false }`
  // is load-bearing: `refreshOneOrder`'s own docblock is explicit that an EDIT
  // (a `PUT`, consuming no approval step) must not stamp `can_action: false`,
  // unlike the ACTION callers in `handleQuickAction` below — an edit save is
  // not an action.
  const handleEditorSaved = useCallback((orderId) => {
    setEditDirtyId(null);
    setExpandedOrders(new Set());
    setOrderDetails(od => { const copy = { ...od }; delete copy[orderId]; return copy; });
    // SILENT — a spinner flip after a save unmounts the list (owner 2026-10-04).
    refreshOneOrder(orderId, setOrders, setOrderDetails, { clearCanAction: false })
      .then(() => fetchOrders(true));
  }, [fetchOrders, setEditDirtyId, setOrders]);

  // ── THE PICKERS' REFERENCE LISTS (2026-10-03) ─────────────────────────────
  // 🔑 ONE shared loader with `useDeptOrderExpansion` — the latch, both
  // effects, every URL, the `null` supplier sentinel and the empty-department
  // clear all live in `hooks/useOrderPickerLists.js` +
  // `utils/orderReferenceLists.js`.
  // 🚨 ORDER IS LOAD-BEARING AND THIS PAGE IS THE OPPOSITE WAY ROUND TO
  // DEPARTMENT ORDERS: reference data FIRST here, accounting second. Hook call
  // order is effect order is REQUEST order, and this page's parity goldens
  // record the recorded call list — four of them went red when a single shared
  // hook imposed one fixed order. Do not reorder these calls, and do not fold
  // them into one hook; the measurement is in the hook's docblock.
  // 🔀 `deferReferenceData` (2026-09-27) is still this hook's prop; it and
  // `expandedOrders` arm the latch.
  const referenceArmed = useOrderReferenceArmed({ deferReferenceData, expandedOrders });
  useOrderReferenceData({
    referenceArmed,
    setProductions,
    setSuppliers,
    setSubDepartments,
    setGags,
    setSupplierSuggestions,
    setLoadFailures,
  });

  // ── WHY THE ACCOUNTING LISTS ARE SCOPED TO THE *ACTIVE* DEPARTMENT ────────
  //
  // ✅ **RESOLVED, NOT JUST NARROWED (2026-08-16).** This effect used to carry
  // a KNOWN LIMITATION here: this page listed the viewer's orders across
  // EVERY department they belong to, so a row whose `department_id` differed
  // from `activeDepartment` could get the wrong department's cost codes /
  // payment types. `fetchOrders`' new client-side display filter (above)
  // means every VISIBLE row is now in `activeDepartment` — the premise that
  // made this a limitation is gone, so this effect body is already correct
  // for every row and needed no change itself.
  //
  // 🔀 **THAT PREMISE EXPIRED AGAIN, 2026-08-31, FOR ALL PRODUCTIONS MODE —
  // read this correction alongside the paragraph above, not instead of it.**
  // In All mode `fetchOrders`' own display filter is BYPASSED BY DESIGN (see
  // its docblock), so the page is back to showing rows across every
  // department the caller belongs to — the exact multi-department premise
  // this effect's 2026-08-16 fix retired, now true again for a different
  // reason. Compounding it: `activeDepartment.department_id` in All mode is
  // the truthy `'all-my-departments'` SENTINEL
  // (`contexts/DepartmentContext.js`), not a real department id — sent to
  // this endpoint it 403s unconditionally
  // (`server/helpers/departmentAuthority.js` refuses a non-integer id BEFORE
  // the admin short-circuit), so EVERY row's cost-code / payment-type control
  // degraded to "couldn't be loaded", even for a viewer with full accounting
  // authority in that row's own real department.
  //
  // 🔻 The `|| isAllDepartments` disjunct on the shared loader's empty branch
  // went with the mode on 2026-08-31; there is no sentinel left to refuse to
  // send.
  useOrderAccountingLists({
    referenceArmed, activeDepartment, setCostCodes, setPaymentTypes, setLoadFailures,
  });

  // ── THE QUICK-ACTION DOUBLE-SUBMIT GUARD (2026-08-13) ────────────────────
  // `hooks/useSubmitGuard.js` — the OrderForm guard, shared. These buttons had
  // no in-flight state at all, so a fast double-click double-fired the PATCH /
  // POST / DELETE (a double `duplicate` is two new orders). The guard's ref
  // drops the second call synchronously; `quickActionBusy` disables the
  // mutating quick-action buttons. One gate for the set on purpose — every
  // mutating branch ends by refreshing the very rows the other buttons act on.
  const { guard: guardQuickAction, busy: quickActionBusy } = useSubmitGuard();
  // Order Completion (2026-08-16) — `{ order, mode: 'complete' | 'reopen' }`
  // while the ALWAYS-SHOWN confirm dialog is open, else null. Same
  // "hold the intent, act on confirm" shape as `DepartmentOrders.js`'s
  // `completionPrompt`, sharing the message builder so the two pages cannot
  // drift on wording.
  const [completionPrompt, setCompletionPrompt] = useState(null);
  // One attempt-identity for the `duplicate` create — reused on retry of a
  // failed attempt, cleared on success (see `hooks/useIdempotencyKey.js`).
  const duplicateIdem = useIdempotencyKey();

  // 🔀 2026-10-03 — THE IN-PAGE REASON DIALOG replaced the two `window.prompt`
  // calls this handler used to make. `requestOrderReason`,
  // `requestRejectionReason` and the `reasonDialog` node all come from
  // `useExpandedOrderCore` above, shared with `useDeptOrderExpansion`.
  // 🪤 The `order` passed below comes from the DETAIL CACHE and may legitimately
  // be absent — a row action can fire from a collapsed row, where nothing has
  // been fetched yet. `OrderReasonDialog` carries a second wording for that
  // case rather than printing `Order ` with a hole in it; a dialog is not the
  // place to discover a cache miss.

  // Quick action handlers for user orders
  const handleQuickAction = useCallback((orderId, action) => guardQuickAction(async () => {
    try {
      if (action === 'cancel') {
        const reason = await requestOrderReason(orderDetails[orderId], 'cancel');
        if (reason === null) return; // User dismissed the dialog
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/cancel`, {
          method: 'PATCH',
          body: JSON.stringify({ cancellation_reason: reason || undefined }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to cancel order');
        }
        // 🚨 THE STALE-ROW BUG — see `utils/refreshOneOrder.js`'s
        // `refreshOrderRow` docblock, and `DepartmentOrders.js`'s twin
        // `handleAction`. Found here 2026-08-27 while chasing a live
        // production report on that page: a bare `refreshOneOrder` is the
        // ONLY refresh and it can fail completely silently, leaving the row
        // wrong with no indication — and here that means a CANCELLED order
        // staying on screen as though it were still live, on the owner's
        // OWN orders page. `refreshOrderRow` closes both the stuck-cache case
        // (the entry is dropped synchronously first, so a failed refresh
        // leaves it EMPTY, never STALE) and the silent-failure case — a full
        // list refetch fallback that fires ONLY when the narrow per-order GET
        // failed (not on every call — `e2e/online-order-action-no-refetch.spec.js`
        // pins that a successful action refreshes only its own row; see the
        // docblock at `refreshOrderRow` for the full reasoning).
        await refreshOrderRow(orderId, { setOrders, setOrderDetails, fetchList: () => fetchOrders(true) });
      } else if (action === 'duplicate') {
        // A protected CREATE: send the attempt's Idempotency-Key (minted on
        // arm, reused on a retry of a failed attempt, cleared on success).
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/duplicate`, {
          method: 'POST',
          headers: { 'Idempotency-Key': duplicateIdem.mint() },
        });
        if (!res.ok) {
          // Preserve the server's message + structured code (matching the
          // DepartmentOrders duplicate branch) so a 409 duplicate_in_flight
          // reaches friendlyErrorMessage as itself rather than as the
          // hard-coded string this line used to throw.
          const data = await res.json().catch(() => ({}));
          const err = new Error(data.error || 'Failed to duplicate order');
          if (data.code) err.code = data.code;
          throw err;
        }
        duplicateIdem.clear();
        const data = await res.json();
        alert(`Order duplicated! New order: ${getOrderNumber(data) || 'Created'}`);
        fetchOrders();
      // 🔴 THE `'restore'` BRANCH IS DELETED (2026-08-12) AND MUST NOT COME
      // BACK IN ANY FORM — not gated, not renamed, not "just for the owner".
      // Product owner: *"the restore is not obvious to what it does"*, and
      // separately *"if a buyer has cancelled an order it would be annoying
      // for a normal user to uncancel it"*. `PATCH /api/orders/:id/uncancel`
      // is deleted server-side in the same change, so this branch could only
      // 404 now.
      //
      // 🚨 THIS WAS THE UNPRIVILEGED DOOR, and it is the reason the fix is
      // worth more than tidiness. Department Orders' Restore item was
      // removed earlier and that closed only the PRIVILEGED door; this
      // branch offered the same round-trip to the order's OWNER. Reproduced
      // in two requests by a plain crew member holding no capability
      // anywhere: cancel your own REJECTED order, restore it here, and it is
      // back in a buyer's To Approve queue at status 'rejected' with no
      // resubmission.
      //
      // 🔑 NOTHING IS LOST. Leaving `cancelled` is the status dropdown's job
      // and always was — see the "Restore IS DELETED" entry in
      // client/src/pages/CLAUDE.md. A mistaken cancel that the dropdown
      // cannot undo means raising a new order; that cost was accepted
      // explicitly.
      // 🔴 THE `'mark-ordered'` / `'mark-delivered'` BRANCHES ARE DELETED
      // (Phase 2, My Orders / Dept Orders convergence). Owner ruling: delivery
      // is the shared per-item received TICKS, and personal/My Space orders
      // (the only orders that ever reached these branches — both menu items
      // were gated on `!detail.department_id`) do NOT use the
      // ordered→delivered flow at all; their ticks staying greyed/inert is
      // accepted (PLAN-myorders-deptorders-convergence.md, "option (a)").
      // `PATCH /api/orders/:id/self-status` is UNTOUCHED server-side — this is
      // a client-only removal of the two callers, not a route retirement.
      //
      // 🔀 **THE "option (a)" RULING WAS REVERSED ON 2026-09-11 — AND THE
      // HISTORY ABOVE IS KEPT DELIBERATELY, NOT DELETED.** Owner: *"it could be
      // that by default on a production a normal user DOES NOT get access to
      // change the status of order which is CORRECT behaviour. but Mysapce is a
      // personal space"*, *"Approve is a real click"*, *"I think they should
      // have access to all of it."* A My Space owner now runs the FULL
      // lifecycle on their own order.
      //
      // 🔑 READ WHAT DID **NOT** COME BACK. These two branches are still
      // deleted and must stay deleted. They drove `PATCH /:id/self-status`, a
      // SECOND private lifecycle that only ever served the legacy
      // NULL-`department_id` "solo" shape — it refuses any order carrying a
      // department, and a My Space order always carries its personal
      // department's, so the gate above (`!detail.department_id`) meant they
      // never fired on a My Space order in the first place. What replaced them
      // is the ONE shared lifecycle: the `statusJumpNode` status override
      // below, `InlineStatusJump` on `PATCH /:id/status`, the same control
      // `DepartmentOrders.js` renders, gated on the server-computed
      // `detail.can_manage`. Delivery is STILL the per-item received ticks —
      // that half of the ruling stands untouched; what changed is that the
      // ticks are now REACHABLE, because the owner can finally move the order
      // to `ordered`.
      //
      // ── THE LIFECYCLE MENU ITEMS (2026-09-11) — Pete: *"Approve is a real
      //    click"*, asked after seeing that the ONLY way to progress a My Space
      //    order was the Override dropdown in the footer. A status override is
      //    a manager's ESCAPE HATCH; it is not what approving an order looks
      //    like, and offering it as the only route made the owner's own space
      //    behave like a broken department.
      //
      // 🚨 ALL THREE GO THROUGH `PATCH /api/orders/:id/status`, WHICH IS *NOT*
      //    THE ENDPOINT `DepartmentOrders.js` USES FOR APPROVE AND REJECT — AND
      //    IT CANNOT BE. That page posts to `/api/approvals/orders/:id/approve`
      //    and `…/reject`, which advance a real chain one step at a time;
      //    `server/routes/approvals.js` answers 404 `'No approval workflow for
      //    this order'` when the order has no `order_approvals` row, and a My
      //    Space order raised since 2026-09-11 has NONE — `POST /api/orders`
      //    deliberately builds no chain and notifies nobody when the landing
      //    department is personal. So Approve on this page would 404 on every
      //    order it is offered for. `PATCH /:id/status` is correct for BOTH
      //    populations: it is the same route the override footer above already
      //    drives, and it RESOLVES whatever chain a LEGACY My Space order still
      //    carries (`order-status-jump-resolves-orphaned-steps.test.js`).
      //
      // 🪤 NARROW SEMANTICS — NO `reset_side_state`, DELIBERATELY DIFFERENT FROM
      //    THE FOOTER THAT HITS THE SAME ROUTE. `InlineStatusJump` always sends
      //    it, and with a target of `ordered` the server then runs
      //    `UPDATE order_items SET is_received = 0, received_date = NULL,
      //    received_by = NULL`, so a `Mark Ordered` carrying it would silently
      //    wipe every received tick on a part-delivered order.
      //    `DepartmentOrders.js`'s own `Mark Ordered` has never sent it; these
      //    items mirror that page's narrow button semantics, not the override's
      //    broad ones. Do NOT "make them consistent".
      //
      // 🔑 The REFRESH is `mergeStatusJumpResult`, the same merge the footer
      //    uses — not `refreshOrderRow`. The route RETURNS the updated row, so
      //    there is no second fetch that could fail silently and leave the row
      //    STALE (the class `refreshOrderRow` exists to close); and the item and
      //    the footer must leave the page in the SAME state, because they
      //    perform the same server action.
      } else if (action === 'approve' || action === 'reject' || action === 'ordered') {
        const body = { status: { approve: 'approved', reject: 'rejected', ordered: 'ordered' }[action] };
        if (action === 'reject') {
          // 🔑 The SAME dialog `DepartmentOrders.js`'s reject branch uses —
          // one `OrderReasonDialog`, one wording — including the `|| 'Rejected'`
          // fallback for an empty string. Only the FIELD differs
          // (`rejection_reason`, which `statusChangeSchema` accepts, where the
          // approvals route takes `notes`). 🚨 `=== null` is DISMISSED and sends
          // NOTHING: a typed answer is a string, `null` is a withdrawn gesture,
          // and collapsing the two would reject an order the user backed out
          // of. ⚠️ The `|| 'Rejected'` arm is now UNREACHABLE from this door —
          // the reject dialog's Confirm is disabled until the trimmed reason is
          // non-empty — and is kept because it is the server's own default
          // shape and this is not the only writer of the field.
          const reason = await requestOrderReason(orderDetails[orderId], 'reject');
          if (reason === null) return;
          body.rejection_reason = reason || 'Rejected';
        }
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/status`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const err = new Error(data.error || 'Failed to change status');
          if (data.code) err.code = data.code;
          throw err;
        }
        const updated = await res.json();
        mergeStatusJumpResult(updated, setOrders, setOrderDetails);
        // Nudge the bell + sidebar dots at once, as Dept Orders' Approve/Reject
        // do (owner 2026-09-29) — otherwise they wait for the 15 s poll.
        window.dispatchEvent(new Event('sfx-approval-change'));
      // NAVIGATING actions (`view`/`edit`/`upload`) unmount the panel, so they
      // destroy an open inline edit BY INTENT — guarded here exactly as
      // `DepartmentOrders.js` guards its own navigating actions (asks when
      // dirty, then navigates — 2026-09-28).
      } else if (action === 'view') {
        continueWhenClean(() => navigate(`/orders/${orderId}`));
      } else if (action === 'upload') {
        continueWhenClean(() => navigate(`/orders/${orderId}?upload=1`));
      } else if (action === 'complete') {
        // Order Completion (2026-08-16) — the ALWAYS-SHOWN ConfirmDialog gate
        // (`completionPrompt`) has already run before this is reached. The
        // server enforces status + authority only, matching
        // `PATCH /api/orders/:id/complete`.
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/complete`, {
          method: 'PATCH',
          body: JSON.stringify({ complete: true }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to mark order complete');
        }
        // Same stale-row class as `cancel` above — see `refreshOrderRow`.
        await refreshOrderRow(orderId, { setOrders, setOrderDetails, fetchList: () => fetchOrders(true) });
      } else if (action === 'reopen') {
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/complete`, {
          method: 'PATCH',
          body: JSON.stringify({ complete: false }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to re-open order');
        }
        // Same stale-row class as `cancel` above — see `refreshOrderRow`.
        await refreshOrderRow(orderId, { setOrders, setOrderDetails, fetchList: () => fetchOrders(true) });
      } else if (action === 'delete-order') {
        if (!window.confirm('Are you sure you want to permanently delete this order? This cannot be undone.')) return;
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}`, {
          method: 'DELETE',
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to delete order');
        }
        // Remove from list in place — no full re-fetch needed.
        setOrders(prev => prev.filter(o => o.id !== orderId));
        setOrderDetails(od => { const copy = { ...od }; delete copy[orderId]; return copy; });
      }
    } catch (err) {
      // Through the shared classifier so structured codes (duplicate_in_flight,
      // version_locked) surface as instructions rather than server internals.
      alert(friendlyErrorMessage(err));
    }
  }), [currentUser, navigate, fetchOrders, guardQuickAction, duplicateIdem, continueWhenClean, setOrders, requestOrderReason, orderDetails]);

  // Toggle expand and lazy-load order details.
  //
  // ── Dirty guard (expand-to-edit) — the `DepartmentOrders.js` answer ───────
  // Only one row is open at a time (accordion), so expanding B while A is
  // dirty would discard A's edits. Collapsing THE DIRTY ROW ITSELF, and (since
  // 2026-09-28) expanding B, both open the inline editor's own three-way
  // prompt (`OrderInlineEditor.js`); B then opens on Save (ok) or Discard.
  const toggleExpand = useCallback((orderId, e) => {
    if (e) e.stopPropagation();
    if (editDirtyId !== null) {
      continueWhenClean(editDirtyId === orderId ? null : () => toggleExpandRef.current(orderId));
      return;
    }
    setExpandedOrders(prev => {
      if (prev.has(orderId)) {
        return new Set();
      } else {
        const next = new Set([orderId]);
        // Fetch details if not already loaded
        if (!orderDetails[orderId] && !detailsLoading[orderId]) {
          setDetailsLoading(dl => ({ ...dl, [orderId]: true }));
          apiGet(`${API_BASE}/api/orders/${orderId}`)
            // W4 fix: a non-ok response (e.g. a stale-department 403) must NOT be
            // handed to `res.json()` and written into the cache — its body would
            // render as though it were the order (see `refreshOneOrder.js`'s
            // "fail to EMPTY, never fail to STALE" rule). Throwing here routes a
            // non-ok response into the same `.catch()` a network exception
            // already takes, which leaves the cache entry ABSENT — exactly what
            // this guard's own `!orderDetails[orderId]` re-fetches on the next
            // expand, and what the row's pre-existing "Failed to load order
            // details." fallback (OrderTableView.js) already
            // renders for.
            .then(res => {
              if (!res.ok) throw new Error(`Failed to load order ${orderId}: ${res.status}`);
              return res.json();
            })
            .then(data => {
              setOrderDetails(od => ({ ...od, [orderId]: data }));
              setDetailsLoading(dl => ({ ...dl, [orderId]: false }));
            })
            .catch(() => {
              setDetailsLoading(dl => ({ ...dl, [orderId]: false }));
            });
        }
        return next;
      }
    });
  }, [orderDetails, detailsLoading, continueWhenClean, editDirtyId]);
  toggleExpandRef.current = toggleExpand;

  // ── SHARED EXPANDED-ROW WIRING (Phase 2, My Orders / Dept Orders
  // convergence) ────────────────────────────────────────────────────────────
  // MyOrders now renders through the SAME shared `OrderExpandedRow` component
  // Department Orders uses (`components/OrderExpandedRow.js`) rather than its
  // own bespoke panel — see the `expandedContent` build inside
  // `paginatedOrders.map(...)` below. `canUploadDelivery` stays simply `true`:
  // every order here is the viewer's OWN (`GET /api/orders?user_id=<self>`),
  // so the server admits the owner for delivery upload regardless of this
  // page's new inline editor.
  // (`handleOrderFilesChanged` comes from `useExpandedOrderCore` since
  // 2026-10-03 — this copy and Department Orders' were byte-identical.)
  // The attachments panel's `onFilesChanged` — `useDeptOrderExpansion`'s twin
  // (the hires row's `file_count` paperclip, 2026-09-29).
  const handleAttachmentsChanged = useCallback((orderId, freshDetail) => {
    handleOrderFilesChanged(orderId, freshDetail);
    if (onOrderMutated && orderId && freshDetail) onOrderMutated();
  }, [handleOrderFilesChanged, onOrderMutated]);

  // 🔴 W1 close-out (2026-08-27) — `utils/receivedItems.js` calls this when a
  // tick/receive-all PATCH SUCCEEDS but the follow-up `GET /api/orders/:id`
  // refresh does not (either shape: a rejected `apiGet` or a resolved non-ok
  // response). "Fail to EMPTY, never fail to STALE": the entry is DELETED,
  // not left holding whatever it held before the mutation, so `toggleExpand`'s
  // `!orderDetails[orderId] && !detailsLoading[orderId]` guard re-fetches on
  // the next re-expand and the row's pre-existing "Failed to load order
  // details." fallback (`OrderTableView.js` — not new, and
  // not part of W4; it already existed for a thrown/rejected fetch) covers the
  // gap in between. Same delete shape `handleEditorSaved` already uses below.
  //
  // 🚨 DELIBERATELY NOT `handleOrderFilesChanged`. That callback is a hard
  // no-op on a falsy `freshDetail` — correct for ITS job (an attachment
  // upload's success handler must never overwrite a good cache entry with
  // nothing), but exactly why it CANNOT double as an "invalidate this entry"
  // signal: calling it with `null` here would silently do nothing and leave
  // the stale entry in place, which is the bug this closes. Two different
  // callbacks for two different claims about the cache — do not merge them.
  // (Both now live in `useExpandedOrderCore`, which carries this reasoning.)

  // ── RECEIVED TICKS IN THE LINE-ITEM ROWS (2026-08-15) ────────────────────
  // The owner's self-service surface: every order here is the viewer's OWN
  // (`GET /api/orders?user_id=<self>`), and the server's received gate admits
  // the owner — so ticking needs no role at all, only a delivery-stage status.
  // The standalone "Items received" block in the embedded OrderExpandedDetails
  // is retired; the SAME mutations now run from this page through
  // `makeReceivedItemHandlers` — ONE shared implementation with
  // `DepartmentOrders.js`, not a copy.
  const [receivingOrderId, setReceivingOrderId] = useState(null);
  const receivedHandlers = useMemo(() => makeReceivedItemHandlers({
    onDetailRefreshed: handleOrderFilesChanged,
    onDetailInvalidated: handleOrderDetailInvalidated,
    onListRefresh: handleItemsReceived,
    onBusyChange: (orderId, busy) => {
      setReceivingOrderId(busy ? orderId : null);
      if (onTickBusyChange) onTickBusyChange(orderId, busy);
    },
    onError: (err) => toastError(err),
  }), [handleOrderFilesChanged, handleOrderDetailInvalidated, handleItemsReceived, onTickBusyChange, toastError]);

  // 🔀 EXTRACTED 2026-10-03 INTO `hooks/useExpandedOrderCore.js`, which
  // Department Orders and Approvals call too: `handlePoke`,
  // `updateOrderListFromItem`, `handleCostCodeChange`,
  // `handlePaymentTypeChange`, `handleItemNotifyUpdated`,
  // `handlePoNumberSaved` and `handleExpectedDeliverySaved`.
  // 🪤 The block that stood here said these were *"Byte-identical to
  // `DepartmentOrders.js`'s four handlers of the same name; copied rather than
  // imported because they close over this page's own `orders`/`orderDetails`
  // state."* The second half was the reason not to share them, and it was
  // wrong: the shared hook OWNS `orderDetails` and takes `setOrders`, so there
  // is nothing left to close over. 🔻 The first half was not quite true either
  // — this page's `handlePoke` listed `currentUser` in a dependency array that
  // its body never read (dropped in the move; behaviour unchanged).

  /**
   * THE EXPANDED ROW for one list row — the `expandedContent` node the page
   * hands to whichever row component is active. The body below is the page's
   * per-row block, moved verbatim.
   */
  const renderExpanded = (order) => {
            const detail = orderDetails[order.id];


            // 📦 Received ticks — the viewer OWNS every order on this page, so
            // the server's received gate admits them unconditionally; the only
            // predicate is the DATA (delivery-stage status, via the SHARED
            // constant). A `submitted` or `rejected` order shows no tick.
            const showReceivedTicks = !!detail && DELIVERY_STAGE_STATUSES.includes(detail.status);
            // 📦 Same as DepartmentOrders.js: `canReceiveNow` is the STATUS half
            // (`RECEIVE_ENABLED_STATUSES`, approved-or-later since the per-LINE
            // gate of 2026-10-28); the line half is `deliveredControlState`.
            const canReceiveNow = !!detail && RECEIVE_ENABLED_STATUSES.includes(detail.status);
            const receivingThisOrder = receivingOrderId === order.id;
            // ⚖️ GOODS ONLY (owner ruling 2026-09-19) — the twin of the block
            // in `DepartmentOrders.js`. They were byte-identical and are now
            // one helper; see the note there.
            const { received: receivedCount, ordered: orderedCount, total: receivableCount } =
              countDelivery(detail && detail.items);
            const allItemsReceived = !!detail && allGoodsReceived(detail.items);
            // 🔀 PER-LINE "ORDERED" (2026-10-28) — the requester may RECEIVE
            // their own order's lines but ORDER them only with order management
            // in its dept or as its My Space owner (`requireOrderManagement`),
            // which is exactly `canManageOrder`. Resolved here, once.
            const canOrderItems = !!detail && canManageOrder(detail);
            const someAwaitingDelivery = orderedCount > receivedCount;

            // Shared predicate — hides the control when the viewer IS the approver
            // (compared by user_id, never by name). See utils/orderStepInfo.js.
            const canPoke = canPokeOrder(order, currentUserId);

            // ── SHARED EXPANDED-ROW GATES (Phase 2) ───────────────────────
            // `canManage` / `showInlineEditor` resolved HERE, exactly the way
            // `DepartmentOrders.js` resolves them — `OrderExpandedRow`
            // RESOLVES NOTHING. `canManage` is generally FALSE for the
            // viewer's own orders (they are the requester, not the dept
            // manager) — that is what gates off the PO editor / line-item
            // accounting selects / item-actions menu column naturally, with
            // no page-name special-case. It is occasionally TRUE — the same
            // rare "buyer viewing their own order" case that already gates
            // `Mark as Complete` below — and when it is, this page grants
            // exactly what Department Orders would grant that same person,
            // which is not a widening.
            const canManage = !!detail && canManageOrder(detail);
            // `detail.can_edit === true` — a requester's OWN editable orders
            // (rejected/draft). Read-only panel otherwise. (A `isTableLayout`
            // term went with the order cards, 2026-10-18.)
            const showInlineEditor = !!detail && detail.can_edit === true;

            // Internal notes — DISPLAY-ONLY on this page (no editor; the buyer
            // who wrote the note edits it from Department Orders). Handed to
            // `OrderExpandedRow`'s `adminNotesNode` slot, which since 2026-09-13
            // routes it into `OrderForm`'s `orderDetailsRow2Rest` — the SECOND
            // cell of Order Details row 2, beside Expected Delivery, under PO
            // Number (owner: *"Internal notes goes next to Expected delivery
            // date"*). It was paired beside Expected Delivery in a narrow/wide
            // row until earlier the same day.
            // 🔑 The `detail.admin_notes ?` guard is load-bearing, not belt-and-
            // braces: `InternalNotesReadOnly` returns null for an empty note, but
            // the ELEMENT is truthy, and a truthy node that renders nothing is
            // the phantom-child defect recorded in `OrderExpandedRow.js`.
            const adminNotesNode = detail && detail.admin_notes ? (
              <InternalNotesReadOnly notes={detail.admin_notes} />
            ) : null;

            // ── THE RETAINED ACTIONS (Poke / Actions ▾) ───────────────────
            // Extracted into a builder so the SAME node (bar its wrapper
            // class) can serve both `quickActionsRow` (the read-only panel's
            // footer) and `quickActionsHeaderGroup` (the inline editor's
            // header row) — the same `buildQuickActions(wrapperClass)` shape
            // `DepartmentOrders.js` uses. Only ONE of the two ever renders at
            // once (`OrderExpandedRow` picks the branch), so calling this
            // twice never double-mounts the menu.
            //
            // 🚨 EVERY GATE BELOW IS VERBATIM WHAT IT WAS BEFORE THIS PHASE —
            // relocated, not rewritten. The two exceptions are both REMOVALS,
            // not additions: `mark-ordered`/`mark-delivered` are gone (see the
            // note at their old branch in `handleQuickAction` above), and the
            // navigating items (`view`, `edit`) now go through
            // `continueWhenClean()` first — required by the inline editor this phase adds, matching
            // `DepartmentOrders.js`'s treatment of its own navigating actions.
            //
            // 🪤 GUARDED ON `detail`, matching the ORIGINAL structure where
            // this whole block sat inside `detail ? (…) : null` — `detail` is
            // still loading (lazy-fetched on expand) for a split second, and
            // every gate below reads `detail.department_id` / `detail.status`
            // unconditionally.
            // ── THE STATUS OVERRIDE (2026-09-11) ──────────────────────────
            // Owner ruling: *"Approve is a real click as the actual buyer may
            // sit on the order for ages and not approve it so having the
            // ability to click it is what we want."*
            //
            // 🔑 THIS IS `DepartmentOrders.js`'s `statusJumpNode`, REUSED — the
            // same component, the same gate, the same handler shape, rendered
            // in the same place (the actions menu's FOOTER, under a separator).
            // It is a `<select>` you SET, not an action you fire, which is why
            // it is the footer and not an item; `InlineStatusJump`'s own
            // docblock argues that, and reimplementing its confirm / reject-
            // reason / forward-jump-reason flow as menu items is exactly what
            // that docblock exists to prevent. House rule: expand a good idea
            // as SHARED CODE, never by copying.
            //
            // 🔴 IT REPLACES THE DELETED `mark-ordered` / `mark-delivered` MENU
            // BRANCHES AND DOES NOT RESTORE THEM. Those were a second, private
            // lifecycle on `PATCH /:id/self-status`; this drives the ONE
            // lifecycle every order uses, `PATCH /:id/status`. Delivery is
            // still the per-item received ticks by ruling — the dropdown's
            // `delivered`/`partially_delivered` options are the same ones
            // Department Orders offers, and the ticks remain the normal route.
            //
            // 🚨 TWO TERMS — AUTHORITY **AND** LOCATION — AND THE SECOND WAS
            // MISSING WHEN THIS SHIPPED (corrected 2026-09-11, adversarial
            // review of `bc3e283c`). The first version read
            // `detail && canManageOrder(detail)` and the reasoning beside it
            // said the gate "is FALSE on an ordinary department order exactly
            // as it was before this change — a requester is not their
            // department's order manager". **That premise is false on THIS
            // page.** Every row here is the viewer's OWN order, so a department
            // BUYER or SUPERVISOR looking at their own order carries
            // `can_manage: true` — and the authority term alone handed them a
            // brand-new status override on a real department order, whose
            // `cancelled` option reaches `PATCH /:id/cancel`. Reproduced on the
            // §6.1 fixture: override present, all seven statuses offered, the
            // cancel prompt and the PATCH both fired.
            //
            // 🔑 IT IS THE IDENTICAL DEFECT THE CANCEL MENU ITEM HAD, one
            // control along, and it needs the identical scope limiter — which
            // is the useful part: `canManageOrder` answers WHO, never WHERE, so
            // ANY control this change newly lights up on this page needs
            // `isPersonalWorkspace` beside it. Owner ruling: *"Make sure that
            // NO access, state change etc changes for any user in a normal
            // department. THis update should ONLY effect users in MySpace."*
            //
            // ⚠️ `isPersonalWorkspace` is `activeDepartment.production_is_personal`
            // and is NOT an authority term — see its derivation at the top of
            // this component for why no per-row field is needed.
            const statusJumpNode = detail && canManageOrder(detail) && isPersonalWorkspace ? (
              <div data-testid={`my-menu-status-row-${order.id}`}>
                <InlineStatusJump
                  order={detail}
                  // 🔑 `mergeStatusJumpResult`, SHARED with `DepartmentOrders.js`
                  // — the second caller is what made it a helper rather than a
                  // second copy (Code Unification Principle; the
                  // semantic-duplication ratchet refuses the copy).
                  onSaved={handleStatusJumpSaved}
                  onCancelClick={() => handleQuickAction(order.id, 'cancel')}
                  // 🔀 2026-10-03 — the rejection reason is collected in the
                  // shared `OrderReasonDialog` instead of a `window.prompt`,
                  // the same wiring `DepartmentOrders.js` has. This page's
                  // copy was the caller `InlineStatusJump`'s docblock named as
                  // still using the prompt fallback; it no longer does.
                  requestRejectionReason={requestRejectionReason}
                  testId={`my-status-jump-${order.id}`}
                />
              </div>
            ) : null;

            // The Cancel item's gate — see its long comment in the menu below.
            // Also handed to the inline editor (`onRequestCancelOrder`), so
            // removing the form's only line offers exactly this cancel.
            const viewerMayCancel = !!detail && ((canManageOrder(detail) && isPersonalWorkspace)
              ? managerMayCancelOrder(detail)
              : requesterMayCancelOrder(detail));

            const buildQuickActions = (wrapperClass) => !detail ? null : (
              <div className={wrapperClass}>
                {/* 🔀 Poke Approver on a PHONE is a menu item now (owner,
                    2026-10-01) — see `mobilePokeItem` in the items below. Desktop
                    keeps its inline control beside the Next Step text. */}
                <OrderActionsMenu
                  triggerTestId={`my-actions-menu-${order.id}`}
                  panelTestId={`my-actions-panel-${order.id}`}
                  ariaLabel={`Actions for order ${getOrderNumber(order)}`}
                  idPrefix={`my-actions-${order.id}`}
                  // Flat mode (this page passes no `categories`), so the
                  // top-level `footer` is what renders — see
                  // `OrderActionsMenu`'s flat branch.
                  // 🔀 2026-09-28 — on the HIRES pages only, `withHireMenu`
                  // turns this flat menu into the hires sections
                  // (`components/hires/hireOrderMenu.js`); with no
                  // `hireActions` it returns this object unchanged, so My
                  // Orders renders exactly this menu.
                  {...withHireMenu(hireActions, order, { dirty: editDirtyId === order.id }, {
                  footer: statusJumpNode,
                  items: [
                    // POKE — a menu item at EVERY width. Phone since 2026-10-01
                    // (it was a separate `md:hidden` button); desktop since
                    // 2026-10-18, when the order cards and their inline Poke
                    // were retired (owner). Gate `canPoke` = `canPokeOrder`.
                    // The testid keeps its historical `-mobile-` name.
                    canPoke ? {
                      key: 'poke',
                      label: pokeLoading[order.id] ? 'Sending...' : pokeLabel(order),
                      tone: 'nav',
                      testId: `poke-button-mobile-${order.id}`,
                      title: pokeTitle(order),
                      disabled: !!pokeLoading[order.id],
                      onClick: (e) => handlePoke(order.id, e),
                    } : null,
                    // 🔻 `View Full Details` REMOVED FROM MY ORDERS (owner,
                    // 2026-10-04: "seems like the expanded order gives us the
                    // info") — removed from all three order list Actions
                    // menus in the same change. KEPT for HIRES ONLY: My
                    // Hires runs this same hook with `hireActions` set, and
                    // `hireOrderMenu.js#arrangeHireOrderMenu` relabels this
                    // exact item to "View in orders" — the hires page's one
                    // route to /orders/:id (its PO/Order cell no longer
                    // links there). Pre-deletion SHA 204a6b95.
                    hireActions ? {
                      key: 'view',
                      label: 'View Full Details',
                      tone: 'nav',
                      testId: `my-menu-view-full-${order.id}`,
                      title: 'Open the full order detail page',
                      onClick: () => continueWhenClean(() => navigate(`/orders/${order.id}`)),
                    } : null,
                    // 🔻 `Edit` DELETED 2026-10-09 (owner, demo run-through:
                    // "remove"). It navigated to the old `/orders/:id/edit`
                    // page (orders with no department only); the expanded row
                    // is the editable order wherever `can_edit` is true.
                    // Pre-deletion SHA b9686c07.
                    // ── THE LIFECYCLE ITEMS (2026-09-11) — Approve · Reject ·
                    //    Mark Ordered, in DepartmentOrders' own order, with its
                    //    own labels, tones and categories, from the SHARED
                    //    descriptors in `constants/orderLifecycleMenuItems.js`.
                    //    Pete: *"Approve is a real click"* — the status
                    //    override in the footer is the escape hatch, not the
                    //    normal route, and until now it was the only one here.
                    //
                    // 🚨 BOTH GATE TERMS ARE LOAD-BEARING AND THIS IS THE SAME
                    //    LIMITER `statusJumpNode` CARRIES — `canManageOrder`
                    //    ANSWERS *WHO*, NEVER *WHERE*. Every row on this page is
                    //    the viewer's own, so a DEPARTMENT MANAGER looking at
                    //    their own department order also has `can_manage: true`;
                    //    the authority term alone would hand them a second,
                    //    unreviewed route onto `approved`/`rejected`/`ordered`
                    //    OUTSIDE My Space, which the ruling forbids (*"Make sure
                    //    that NO access, state change etc changes for any user in
                    //    a normal department"*). That is the identical widening
                    //    the reviewer reproduced on `statusJumpNode` the same
                    //    day. `isPersonalWorkspace` is
                    //    `activeDepartment.production_is_personal` — never
                    //    `department_is_personal` (they disagree on 10 dev rows),
                    //    never a name match.
                    //
                    // 🔴 A DEPARTMENT REQUESTER IS UNAFFECTED IN BOTH TERMS: a
                    //    plain `user` has `can_manage: false` from the server, so
                    //    their menu is byte-identical to what it was. Two
                    //    independent reasons, deliberately — either alone would
                    //    do, and the pair is what makes the ruling structural.
                    //
                    // ⚠️ The STATUS terms mirror `DepartmentOrders.js` exactly:
                    //    Approve/Reject at `submitted` only (its
                    //    `canApproveReject` ends `order.status === 'submitted'`),
                    //    Mark Ordered at `approved` only. Anything else stays
                    //    reachable through the override footer, which is
                    //    unchanged.
                    canManageOrder(detail) && isPersonalWorkspace && detail.status === 'submitted' && orderLifecycleMenuItem('approve', {
                      testIdPrefix: 'my',
                      orderId: order.id,
                      disabled: quickActionBusy,
                      // 🪤 TITLE OVERRIDE, AND IT IS A CORRECTNESS FIX RATHER
                      // THAN A PREFERENCE. The shared default reads "advance it
                      // to the next approval step"; a My Space order has NO
                      // approval chain at all, so that sentence would be false on
                      // every order this item is ever offered for.
                      title: 'Approve this order yourself — this is your own space, so there is no one else to approve it',
                      onClick: () => handleQuickAction(order.id, 'approve'),
                    }),
                    canManageOrder(detail) && isPersonalWorkspace && detail.status === 'submitted' && orderLifecycleMenuItem('reject', {
                      testIdPrefix: 'my',
                      orderId: order.id,
                      disabled: quickActionBusy,
                      // Same reason as Approve's: there is no "submitter" other
                      // than the viewer to send it back to.
                      title: 'Reject this order — you can then edit it and resubmit',
                      onClick: () => handleQuickAction(order.id, 'reject'),
                    }),
                    canManageOrder(detail) && isPersonalWorkspace && detail.status === 'approved' && orderLifecycleMenuItem('ordered', {
                      testIdPrefix: 'my',
                      orderId: order.id,
                      disabled: quickActionBusy,
                      // The shared default is already true here — an order IS
                      // placed with a supplier from My Space exactly as from a
                      // department — so no override.
                      onClick: () => handleQuickAction(order.id, 'ordered'),
                    }),
                    // 🔀 "Mark all Delivered" (2026-09-13, Pete) — the SAME item
                    // `DepartmentOrders.js`'s Next-step group carries, in
                    // lifecycle position: after Mark Ordered, before Mark as
                    // Complete. This page is FLAT (no `categories`), so there is
                    // no heading to place it under — see the "no headings"
                    // comment on `OrderActionsMenu` above.
                    // 🚨 GATED BY THE SAME PREDICATES THIS PAGE ALREADY RESOLVES
                    // FOR ITS OWN TICK CLUSTER (`showReceivedTicks` /
                    // `canReceiveNow` / `receivingThisOrder`, above) — never
                    // re-derived. This page shows only the viewer's OWN orders,
                    // so there is no separate "who may tick" predicate the way
                    // `canReceiveItems` is on `DepartmentOrders.js`;
                    // `showReceivedTicks` already IS that answer here.
                    // 🚨 MECHANISM is the tick cluster's own
                    // `receivedHandlers.receiveAll` — the SAME PATCH the header
                    // checkbox calls — never the status override and never a new
                    // endpoint. This page's mutating items are NOT wrapped in a
                    // save-then-act prompt (unlike DepartmentOrders/Approvals —
                    // `handleQuickAction` above has none either), so this item
                    // matches its lifecycle siblings and calls the handler
                    // directly.
                    showReceivedTicks && !allItemsReceived && someAwaitingDelivery && {
                      key: 'receive-all',
                      label: 'Mark all Delivered',
                      tone: 'deliver',
                      disabled: !canReceiveNow || quickActionBusy || receivingThisOrder,
                      title: !canReceiveNow
                        ? RECEIVING_LOCKED_TOOLTIP
                        : 'Tick every line item on this order as delivered',
                      testId: `my-menu-receive-all-${order.id}`,
                      onClick: () => receivedHandlers.receiveAll(order.id, true),
                    },
                    // Download PDF / Print… / Share… — `OrderPdfButton`'s three
                    // actions, flattened, exactly as the other two order pages.
                    {
                      key: 'pdf',
                      render: (close) => (
                        <OrderPdfMenuItems
                          order={detail}
                          onDone={close}
                          testIdPrefix={`my-${order.id}`}
                        />
                      ),
                    },
                    // Order Completion (2026-08-16) — a completion-flag
                    // OVERLAY, never a status; gated exactly like the server.
                    canManageOrder(detail)
                      && ['delivered', 'partially_delivered'].includes(detail.status)
                      && !isOrderComplete(detail)
                      && !orderHasHireStillOut(detail.items) && {
                      key: 'complete',
                      label: 'Mark as Complete',
                      tone: 'deliver',
                      disabled: quickActionBusy,
                      testId: `my-menu-complete-${order.id}`,
                      // 🔀 2026-09-30: the Filters "Completed" checkbox this
                      // used to point at is gone — My Orders has a Completed TAB.
                      title: "Mark this order as finished. It keeps its current status, gets a ✓ Complete badge and moves to your Completed tab.",
                      // 🪤 `has_delivery_paperwork` is a LIST-row field, NOT
                      // sent by `GET /api/orders/:id` — read it off `order`.
                      onClick: () => setCompletionPrompt({
                        order: detail,
                        items: detail ? detail.items : undefined,
                        hasDeliveryPaperwork: order.has_delivery_paperwork,
                        mode: 'complete',
                      }),
                    },
                    canManageOrder(detail) && isOrderComplete(detail) && {
                      key: 'reopen',
                      label: 'Re-open',
                      tone: 'progress',
                      disabled: quickActionBusy,
                      testId: `my-menu-reopen-${order.id}`,
                      title: 'Show this order as active again — removes the ✓ Complete badge. Its status is unchanged.',
                      onClick: () => setCompletionPrompt({ order: detail, mode: 'reopen' }),
                    },
                    // 🔀 PERSONAL "Mark as done" / "Restore" (owner ruling
                    // 2026-09-30). NOT the buyer's completion above: it moves
                    // the row into the requester's OWN Completed tab and
                    // changes nothing anyone else sees. The gates read the
                    // LIST row (`my_done` travels only on `GET /api/orders`,
                    // never on the detail) and are conveniences — the server
                    // re-checks requester and status.
                    onPersonalDone && canMarkOrderDone(order, currentUserId) && {
                      key: 'mark-done',
                      label: 'Mark as done',
                      tone: 'deliver',
                      disabled: quickActionBusy,
                      testId: `my-menu-mark-done-${order.id}`,
                      title: MARK_ORDER_DONE_TOOLTIP,
                      onClick: () => onPersonalDone(order, true),
                    },
                    onPersonalDone && canRestoreOrderDone(order, currentUserId) && {
                      key: 'restore-done',
                      label: 'Restore',
                      tone: 'progress',
                      disabled: quickActionBusy,
                      testId: `my-menu-restore-done-${order.id}`,
                      title: 'Moves this order back out of your Completed tab. Its status is unchanged.',
                      onClick: () => onPersonalDone(order, false),
                    },
                    // Cancel — THREE arms, and the first is new (2026-09-11).
                    //
                    // 🔑 IN MY SPACE, THE OWNER GETS THE MANAGER'S GATE, AND
                    // THAT GATE IS `DepartmentOrders.js`'s. Owner ruling: *"I
                    // think they should have access to all of it"* — an outside
                    // buyer places the order and then pulls out, so the owner
                    // has to cancel an ALREADY-ORDERED order, which the
                    // requester arm below stops at `approved`.
                    // `managerMayCancelOrder` (`constants/statuses.js`) is that
                    // page's gate written down: it has no Cancel ITEM at all,
                    // so a manager cancels through the status override's
                    // `cancelled` option, whose only status term is "not the
                    // status it already has". Every status but `cancelled` —
                    // matching the server, which answers 400 "Order is already
                    // cancelled" before it looks at authority at all.
                    //
                    // 🚨 BOTH TERMS ARE LOAD-BEARING AND `canManageOrder` ALONE
                    // IS NOT ENOUGH — MEASURED, NOT REASONED. Every row on this
                    // page is the viewer's own, and a DEPARTMENT MANAGER
                    // looking at their own order also has `can_manage: true`,
                    // so the authority term alone gave them Cancel on a
                    // DELIVERED department order — a real widening outside My
                    // Space, which the ruling forbids ("This update should ONLY
                    // effect users in MySpace"). It went red in
                    // `requiredActions.manifest.test.js` §2's
                    // `my-orders/cancel` negative, whose viewer is exactly that
                    // manager. `isPersonalWorkspace` is the scope limiter, and
                    // it is the SAME `production_is_personal` read this page
                    // already uses for its display filter above — never
                    // `department_is_personal`, never a name match.
                    //
                    // ⚠️ The THIRD arm is the legacy NULL-`department_id` solo
                    // shape and is deliberately untouched — a separate job,
                    // gated on a production count.
                    // 🔀 2026-10-01: the expression is `viewerMayCancel`,
                    // hoisted above `buildQuickActions` so the inline editor's
                    // only-line Remove asks the SAME question (one gate, two
                    // controls); the requester arms are now
                    // `requesterMayCancelOrder` (`constants/statuses.js`).
                    viewerMayCancel && {
                      key: 'cancel',
                      label: 'Cancel Order',
                      tone: 'reject',
                      disabled: quickActionBusy,
                      testId: `my-menu-cancel-${order.id}`,
                      title: 'Cancel this order',
                      onClick: () => handleQuickAction(order.id, 'cancel'),
                    },
                    // 🔴 DO **NOT** RE-ADD A "Restore Order" ITEM HERE — see
                    // the deleted `'restore'` branch note in `handleQuickAction`.

                    // Delete — unassigned users only, permanent removal.
                    !detail.department_id && {
                      key: 'delete',
                      label: 'Delete',
                      tone: 'reject',
                      disabled: quickActionBusy,
                      testId: `my-menu-delete-${order.id}`,
                      title: 'Permanently delete this order',
                      onClick: () => handleQuickAction(order.id, 'delete-order'),
                    },
                  ],
                  })}
                />
              </div>
            );
            // Same wrapper class `DepartmentOrders.js` uses for BOTH slots
            // (2026-08-16) — no `mt-3` on either. The read-only actions now
            // render inside the shared "Order Details" header
            // (`OrderSectionHeader`), which owns its own spacing; the old
            // `mt-3` variant was for the bottom-of-panel placement this
            // supersedes.
            const quickActionsRow = buildQuickActions('grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center');
            const quickActionsHeaderGroup = buildQuickActions('grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center');

            // ── THE SHARED EXPANDED-ROW BODY (Phase 2) ─────────────────────
            // `components/OrderExpandedRow.js` — the SAME component
            // `DepartmentOrders.js` renders through (Phase 1). It RESOLVES
            // NOTHING: every gate above is computed on THIS page and handed
            // down as a prop; moving this JSX grants nobody anything.
            // `testIdPrefix="my"` keeps every existing `my-*` testid intact —
            // distinct from Dept Orders' `dept-*` prefix.
            //
            // 🔻 THE ALL-MODE PER-ORDER LISTS WENT ON 2026-08-31 with the
            // mode. `hooks/useAllModeAccountingLists.js` fetched cost codes
            // and payment types keyed on each ROW's own `department_id`,
            // because in All mode one list could not answer for several
            // departments at once. With one department in view the
            // page-level state below IS that answer, which is what it was
            // before the mode.
            //
            // 🚨 THE PAGE-LEVEL `loadFailures` IS NOT PART OF THAT AND MUST
            // TRAVEL WITH THE ARRAYS IT DESCRIBES. Without it an emptied
            // `costCodes` reaches `OrderForm` as "this department genuinely
            // has no cost codes" and renders a live `<select>` holding only
            // `-- None --`, so a cost-coded line displays as UNCODED — on a
            // money field.
            const expandedContent = detail ? (
              <OrderExpandedRow
                order={order}
                detail={detail}
                showInlineEditor={showInlineEditor}
                canManage={canManage}
                testIdPrefix="my"
                itemExtraActions={hireActions ? (item) => hireActions.lineItems(order, item, { dirty: editDirtyId === order.id }) : null}
                itemHeaderTick={hireActions ? (item) => hireActions.lineTick(order, item, { dirty: editDirtyId === order.id }) : null}
                formatMoney={formatMoney}
                costCodes={costCodes}
                paymentTypes={paymentTypes}
                onCostCodeChange={handleCostCodeChange}
                onPaymentTypeChange={handlePaymentTypeChange}
                onItemNotifyUpdated={handleItemNotifyUpdated}
                onPoNumberSaved={handlePoNumberSaved}
                onExpectedDeliverySaved={handleExpectedDeliverySaved}
                showReceivedTicks={showReceivedTicks}
                canReceiveNow={canReceiveNow}
                receivingThisOrder={receivingThisOrder}
                receivedCount={receivedCount}
                receivableCount={receivableCount}
                allItemsReceived={allItemsReceived}
                onToggleItemReceived={receivedHandlers.toggleItem}
                onReceiveAll={receivedHandlers.receiveAll}
                canOrderItems={canOrderItems}
                orderedCount={orderedCount}
                onToggleItemOrdered={receivedHandlers.toggleOrdered}
                onOrderAll={receivedHandlers.orderAll}
                adminNotesNode={adminNotesNode}
                quickActionsRow={quickActionsRow}
                quickActionsHeaderGroup={quickActionsHeaderGroup}
                // 🔻 `quickActionsPlacement="top"` REMOVED (2026-08-16) — the
                // read-only actions now live inside the shared "Order
                // Details" header for every caller (see `OrderExpandedRow`'s
                // docblock); the prop itself is deleted from the component.
                // Every order here is the viewer's OWN
                // (`GET /api/orders?user_id=<self>`), so the server admits the
                // owner for delivery upload unconditionally — same as before
                // this page had an inline editor.
                canUploadDelivery
                onFilesChanged={handleAttachmentsChanged}
                editor={{
                  productions,
                  suppliers,
                  supplierSuggestions,
                  // Travels WITH the arrays it describes, so a page cannot hand
                  // the form a list without saying whether it landed.
                  loadFailures,
                  gags,
                  subDepartments,
                  userAssignedProductions,
                  collapseRequestSignal,
                  onCollapsePromptResolved: onPromptResolved,
                  onDirtyChange: (isDirty) => handleEditorDirtyChange(order.id, isDirty),
                  onSaved: () => handleEditorSaved(order.id),
                  onCancel: () => { setEditDirtyId(null); setExpandedOrders(new Set()); },
                  // 2026-10-01 — the only-line Remove's "Cancel order" runs the
                  // Cancel item's own flow, behind the Cancel item's own gate.
                  onRequestCancelOrder: viewerMayCancel ? () => handleQuickAction(order.id, 'cancel') : null,
                }}
              />
            ) : null;
            return expandedContent;
  };

  // The page-level dialog the row actions open. The CALLER renders this node
  // once, at page level.
  const dialogs = (
    <>
      {/* 🔀 2026-10-03 — the cancel / reject reason, in the page instead of a
          native `window.prompt`. Built in `useExpandedOrderCore`, so this page
          and Department Orders ask the same question in the same words. */}
      {reasonDialog}

      {/* Order Completion — the ALWAYS-SHOWN confirm dialog (2026-08-16).
          Same message builder as `DepartmentOrders.js`, so the two surfaces
          cannot drift on wording. Continue fires `handleQuickAction`; Cancel
          clears the prompt and writes nothing. */}
      <ConfirmDialog
        open={completionPrompt !== null}
        title={completionPrompt ? (
          completionPrompt.mode === 'complete'
            ? buildCompleteConfirmMessage({
                order: completionPrompt.order,
                items: completionPrompt.items,
                hasDeliveryPaperwork: completionPrompt.hasDeliveryPaperwork,
              }).title
            : buildReopenConfirmMessage({ order: completionPrompt.order }).title
        ) : ''}
        message={completionPrompt ? (
          completionPrompt.mode === 'complete'
            ? buildCompleteConfirmMessage({
                order: completionPrompt.order,
                items: completionPrompt.items,
                hasDeliveryPaperwork: completionPrompt.hasDeliveryPaperwork,
              }).message
            : buildReopenConfirmMessage({ order: completionPrompt.order }).message
        ) : ''}
        confirmLabel={completionPrompt && completionPrompt.mode === 'complete' ? 'Mark Complete' : 'Re-open'}
        onCancel={() => setCompletionPrompt(null)}
        onConfirm={() => {
          const pending = completionPrompt;
          setCompletionPrompt(null);
          if (pending) handleQuickAction(pending.order.id, pending.mode === 'complete' ? 'complete' : 'reopen');
        }}
      />
    </>
  );

  return {
    expandedOrders,
    // Close every opened row (owner 2026-10-03: any tab tap). Callers run it
    // through `continueWhenClean`, so a dirty edit asks first.
    collapseAllOrders: () => setExpandedOrders(new Set()),
    orderDetails,
    detailsLoading,
    continueWhenClean,
    toggleExpand,
    handlePoke,
    pokeLoading,
    renderExpanded,
    dialogs,
    // 2026-09-28 — same as `useDeptOrderExpansion`'s: refresh an order's
    // DETAIL cache after a mutation made outside this hook.
    refreshOrderDetail: (orderId) => refreshOneOrder(orderId, setOrdersFromCaller, setOrderDetails, { clearCanAction: false }),
  };
}
